import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialPanelComerciales } from './panel_comerciales.initial';
import {
    assignFromQueueBatchThunk,
    assignFromQueueThunk,
    loadActivity,
    loadAssignmentQueue,
    loadDealDuplicates,
    loadNotifications,
    loadSnapshot,
    mergeDealsThunk,
    saveSellerConfigThunk,
    saveVipOverrideThunk,
} from './thunks';
import {
    AuditEntry,
    Commercial,
    Team,
    VipAccount,
} from 'src/components/Retention/PanelComerciales/PanelComerciales.types';
import { DuplicatesSearch } from './panel_comerciales.types';

export const panelComercialesSlice = createSlice({
    name: 'panel_comerciales',
    initialState: initialPanelComerciales,
    reducers: {
        upsertTeam: (state, action: PayloadAction<Team>) => {
            const idx = state.snapshot.teams.findIndex((t) => t.name === action.payload.name);
            if (idx >= 0) state.snapshot.teams[idx] = action.payload;
            else state.snapshot.teams.push(action.payload);
        },
        renameTeam: (state, action: PayloadAction<{ from: string; to: string }>) => {
            const { from, to } = action.payload;
            if (from === to) return;
            state.snapshot.teams = state.snapshot.teams.map((t) =>
                t.name === from ? { ...t, name: to, leader: t.leader === from ? to : t.leader } : t,
            );
            state.snapshot.commercials = state.snapshot.commercials.map((c) =>
                c.team === from ? { ...c, team: to } : c,
            );
            state.snapshot.vips = state.snapshot.vips.map((v) =>
                v.team === from ? { ...v, team: to } : v,
            );
        },
        deactivateTeam: (state, action: PayloadAction<string>) => {
            state.snapshot.teams = state.snapshot.teams.map((t) =>
                t.name === action.payload ? { ...t, active: false } : t,
            );
        },
        upsertCommercial: (state, action: PayloadAction<Commercial>) => {
            const idx = state.snapshot.commercials.findIndex((c) => c.id === action.payload.id);
            if (idx >= 0) state.snapshot.commercials[idx] = action.payload;
            else state.snapshot.commercials.push(action.payload);
        },
        upsertVip: (state, action: PayloadAction<VipAccount>) => {
            const idx = state.snapshot.vips.findIndex((v) => v.id === action.payload.id);
            if (idx >= 0) state.snapshot.vips[idx] = action.payload;
            else state.snapshot.vips.push(action.payload);
        },
        removeVip: (state, action: PayloadAction<string>) => {
            state.snapshot.vips = state.snapshot.vips.filter((v) => v.id !== action.payload);
        },
        appendAuditLog: (state, action: PayloadAction<AuditEntry>) => {
            state.snapshot.auditLog = [action.payload, ...state.snapshot.auditLog];
        },
        setDuplicatesSearch: (state, action: PayloadAction<DuplicatesSearch>) => {
            state.dealsStatus.duplicatesSearch = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadSnapshot.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(loadSnapshot.fulfilled, (state, action) => {
            state.loading = false;
            state.snapshot = action.payload;
        });
        builder.addCase(loadSnapshot.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message ?? 'Error cargando snapshot';
        });

        builder.addCase(saveSellerConfigThunk.pending, (state) => {
            state.saving = true;
        });
        builder.addCase(saveSellerConfigThunk.fulfilled, (state) => {
            state.saving = false;
        });
        builder.addCase(saveSellerConfigThunk.rejected, (state, action) => {
            state.saving = false;
            state.error = action.error.message ?? 'Error guardando configuración';
        });

        builder.addCase(saveVipOverrideThunk.pending, (state) => {
            state.saving = true;
        });
        builder.addCase(saveVipOverrideThunk.fulfilled, (state) => {
            state.saving = false;
        });
        builder.addCase(saveVipOverrideThunk.rejected, (state, action) => {
            state.saving = false;
            state.error = action.error.message ?? 'Error guardando VIP';
        });

        // --- Sección "Estado de deals" (Reglas de Marketing jul-2026) ---
        builder.addCase(loadDealDuplicates.pending, (state) => {
            state.dealsStatus.duplicatesLoading = true;
        });
        builder.addCase(loadDealDuplicates.fulfilled, (state, action) => {
            state.dealsStatus.duplicatesLoading = false;
            state.dealsStatus.duplicates = action.payload;
        });
        builder.addCase(loadDealDuplicates.rejected, (state, action) => {
            state.dealsStatus.duplicatesLoading = false;
            state.error = action.error.message ?? 'Error cargando duplicados';
        });

        builder.addCase(loadAssignmentQueue.pending, (state) => {
            state.dealsStatus.queueLoading = true;
        });
        builder.addCase(loadAssignmentQueue.fulfilled, (state, action) => {
            state.dealsStatus.queueLoading = false;
            state.dealsStatus.queue = action.payload;
        });
        builder.addCase(loadAssignmentQueue.rejected, (state, action) => {
            state.dealsStatus.queueLoading = false;
            state.error = action.error.message ?? 'Error cargando la cola';
        });

        builder.addCase(mergeDealsThunk.pending, (state) => {
            state.dealsStatus.actionLoading = true;
        });
        builder.addCase(mergeDealsThunk.fulfilled, (state) => {
            state.dealsStatus.actionLoading = false;
        });
        builder.addCase(mergeDealsThunk.rejected, (state) => {
            // El mensaje legible viaja al toast del caller (unwrap); aquí
            // solo se libera el loading para no bloquear la sección.
            state.dealsStatus.actionLoading = false;
        });

        builder.addCase(assignFromQueueThunk.pending, (state) => {
            state.dealsStatus.actionLoading = true;
        });
        builder.addCase(assignFromQueueThunk.fulfilled, (state) => {
            state.dealsStatus.actionLoading = false;
        });
        builder.addCase(assignFromQueueThunk.rejected, (state) => {
            state.dealsStatus.actionLoading = false;
        });

        builder.addCase(assignFromQueueBatchThunk.pending, (state) => {
            state.dealsStatus.actionLoading = true;
        });
        builder.addCase(assignFromQueueBatchThunk.fulfilled, (state) => {
            state.dealsStatus.actionLoading = false;
        });
        builder.addCase(assignFromQueueBatchThunk.rejected, (state) => {
            state.dealsStatus.actionLoading = false;
        });

        // --- Notificaciones y actividad paginadas (ago-2026) ---
        builder.addCase(loadNotifications.pending, (state) => {
            state.notificationsLoading = true;
        });
        builder.addCase(loadNotifications.fulfilled, (state, action) => {
            state.notificationsLoading = false;
            state.notifications = action.payload;
        });
        builder.addCase(loadNotifications.rejected, (state, action) => {
            state.notificationsLoading = false;
            state.error = action.error.message ?? 'Error cargando notificaciones';
        });

        builder.addCase(loadActivity.pending, (state) => {
            state.activityLoading = true;
        });
        builder.addCase(loadActivity.fulfilled, (state, action) => {
            state.activityLoading = false;
            state.activity = action.payload;
        });
        builder.addCase(loadActivity.rejected, (state, action) => {
            state.activityLoading = false;
            state.error = action.error.message ?? 'Error cargando actividad';
        });
    },
});

export const {
    upsertTeam,
    renameTeam,
    deactivateTeam,
    upsertCommercial,
    upsertVip,
    removeVip,
    appendAuditLog,
    setDuplicatesSearch,
} = panelComercialesSlice.actions;

export default panelComercialesSlice.reducer;
